import {
  Box,
  Typography,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Button,
  Grid,
  Chip,
  Divider,
  Tabs,
  Tab,
  Paper,
} from "@mui/material";
import React, { useState, useEffect } from "react";
import RefreshIcon from "@mui/icons-material/Refresh";
import ScoreIcon from "@mui/icons-material/Score";
import TableChartIcon from "@mui/icons-material/TableChart";
import "./Scores.css";
import { useNavigate } from "react-router-dom";

const ScoresTemplate = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(0);
  const navigate = useNavigate();

  // Google Sheets configuration for each category
  const categorySheets = [
    {
      name: "Bowling Junior",
      sheetUrl:
        "https://docs.google.com/spreadsheets/d/e/2PACX-1vSdD2P37bqng85uoa6t0P61cLKEqvYbfMF7UoagyS5wVn0AQcDzYp4q8llv3GMOzA/pubhtml?widget=true&amp;headers=false",
    },
    {
      name: "Bowling Senior",
      sheetUrl:
        "https://docs.google.com/spreadsheets/d/e/2PACX-1vTWRDqWmL8cCWteWOockCztk-YdKKFClvfy4fOuvesgKk1XsRzEx0CEFMjvqNyv2g/pubhtml?widget=true&amp;headers=false",
    },
    {
      name: "Follow Line Enhanced",
      sheetUrl:
        "https://docs.google.com/spreadsheets/d/e/2PACX-1vSvliig2Sn6Eu8oszDsTTqp93y8DwTqdD3NTsSUbOZr9Eu_VH2twn8oHgtRNJ0Ynw/pubhtml?widget=true&amp;headers=false",
    },
    {
      name: "Follow Line Lego Junior",
      sheetUrl:
        "https://docs.google.com/spreadsheets/d/e/2PACX-1vR2J6A3-Q71v0mntIE1DoWSAkBSwjBvVE3Y7roiQ8SR_iYH4MnrudUc4k_fKy_jHw/pubhtml?widget=true&amp;headers=false",
    },
    {
      name: "Follow Line Lego Senior",
      sheetUrl:
        "https://docs.google.com/spreadsheets/d/e/2PACX-1vRzjDb9jh9EK82FdTc3ACeWRVPFRd4rCjfEgF-zYvFrFtLWYsHXqpzBjQrnfKCxSw/pubhtml?widget=true&amp;headers=false",
    },
    {
      name: "Follow Line Open Junior",
      sheetUrl:
        "https://docs.google.com/spreadsheets/d/e/2PACX-1vS_iHI1py2g8btK1ehcetcOJO32fM8hk82-pcpGl8sdfl_nTkxaAFmur0di1lHahA/pubhtml?widget=true&amp;headers=false",
    },
    {
      name: "Follow Line Open Senior",
      sheetUrl:
        "https://docs.google.com/spreadsheets/d/e/2PACX-1vQcpoZRQeqKkSkKaVmVuhXTZ9APo4bwNqhJcCTlf_BGoBQO-d3zCpVgLlPt3W9URA/pubhtml?widget=true&amp;headers=false",
    },
    {
      name: "Follow Line Open Adult",
      sheetUrl:
        "https://docs.google.com/spreadsheets/d/e/2PACX-1vS-DNDJVyKpXX7jfKI88FvPohvgbFy7DiaQgwvtroHM82wSa2enfxZd0yizHimqdw/pubhtml?widget=true&amp;headers=false",
    },
    {
      name: "Sumo Lego Junior",
      link: "https://challonge.com/mw5v0zqz",
    },
    {
      name: "Sumo Lego Senior",
      link: "https://challonge.com/prgcxinn",
    },
    {
      name: "Sumo Mega",
      link: "https://challonge.com/kd83hgou",
    },
    { name: "Sumo Mini", link: "https://challonge.com/s39ngua4" },
    {
      name: "Puck Collect",
      link: "https://challonge.com/3civ2kvv",
    },
  ];

  useEffect(() => {
    loadScores();
  }, []);

  const loadScores = async () => {
    setLoading(true);
    setError(null);

    try {
      // Simulate loading delay
      await new Promise((resolve) => setTimeout(resolve, 500));
      setLastUpdated(new Date());
    } catch (err) {
      setError("Failed to load scores. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    loadScores();
  };

  const handleCategoryChange = (event, newValue) => {
    if (categorySheets[newValue].link) {
      window.open(categorySheets[newValue].link, "_blank");
    } else {
      setSelectedCategory(newValue);
    }
  };

  // Convert Google Sheets URL to embeddable format
  const getEmbedUrl = (url) => {
    return url;
  };

  return (
    <Box
      sx={{
        width: "100%",
        padding: { xs: "30px 20px", sm: "40px 60px", lg: "50px 120px" },
        maxWidth: "1600px",
        margin: "auto",
        minHeight: "80vh",
      }}
    >
      <Typography
        variant={"h1"}
        sx={{
          textAlign: "center",
          fontSize: { xs: "35px", sm: "45px" },
          fontWeight: "500",
          color: "primary.darker",
        }}
      >
        Competition Scores
      </Typography>
      <Box
        sx={{
          height: "3px",
          maxWidth: { xs: "100px", md: "150px" },
          width: "100%",
          backgroundColor: "primary.main",
          margin: "auto",
          marginBottom: "30px",
        }}
      />

      {/* Header Section */}
      <Card sx={{ marginBottom: "30px" }}>
        <CardContent>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              flexWrap: "wrap",
              gap: "20px",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: "15px" }}>
              <TableChartIcon
                sx={{ color: "primary.main", fontSize: "40px" }}
              />
              <Box>
                <Typography
                  variant="h5"
                  sx={{ color: "primary.darker", fontWeight: "600" }}
                >
                  Live Scoreboard
                </Typography>
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  Real-time competition results and rankings
                </Typography>
              </Box>
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", gap: "15px" }}>
              {lastUpdated && (
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  Last updated: {lastUpdated.toLocaleTimeString()}
                </Typography>
              )}
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={handleRefresh}
                disabled={loading}
                sx={{
                  borderColor: "primary.main",
                  color: "primary.main",
                  "&:hover": {
                    borderColor: "primary.dark",
                    backgroundColor: "primary.light",
                    color: "white",
                  },
                }}
              >
                Refresh
              </Button>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Category Tabs */}
      <Paper sx={{ marginBottom: "0px" }}>
        <Tabs
          value={selectedCategory}
          onChange={handleCategoryChange}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            borderBottom: 1,
            borderColor: "divider",
            "& .MuiTab-root": {
              fontWeight: 600,
              textTransform: "none",
              fontSize: "16px",
              minHeight: "60px",
            },
          }}
        >
          {categorySheets.map((category, index) => (
            <Tab
              key={index}
              label={category.name}
              sx={{
                color:
                  selectedCategory === index
                    ? "primary.main"
                    : "text.secondary",
                "&.Mui-selected": {
                  color: "primary.main",
                  fontWeight: 700,
                },
              }}
            />
          ))}
        </Tabs>
      </Paper>

      {/* Google Sheet Iframe */}
      <Card>
        <CardContent sx={{ padding: 0 }}>
          {loading ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                padding: "60px",
              }}
            >
              <CircularProgress size={60} />
            </Box>
          ) : error ? (
            <Alert severity="error" sx={{ margin: "20px" }}>
              {error}
            </Alert>
          ) : (
            <Box sx={{ position: "relative", width: "100%", height: "80vh" }}>
              {/* <Typography
                variant="h6"
                sx={{
                  padding: "20px 20px 10px 20px",
                  color: "primary.darker",
                  fontWeight: "600",
                  borderBottom: "1px solid #e0e0e0",
                }}
              >
                {categorySheets[selectedCategory].name} -{" "}
                {categorySheets[selectedCategory].description}
              </Typography> */}
              <iframe
                src={getEmbedUrl(categorySheets[selectedCategory].sheetUrl)}
                width="100%"
                height="700px"
                frameBorder="0"
                style={{
                  border: "none",
                  borderRadius: "0 0 8px 8px",
                }}
                title={`${categorySheets[selectedCategory].name} Scores`}
                loading="lazy"
              />
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default ScoresTemplate;
